import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, Fragment, useEffect } from 'react';

import { Box, Grid, Stack, Button, Typography, Chip } from '@mui/material';


import {
    startTour,
    TOUR_TYPES,
    goToNextTour,
    completeTour,
    updateTourStep,
    selectIsSkipped,
    selectTourStatus,
    selectIsCompleted,
    selectTutorialPreference,
} from 'store/slices/tour.slice';

import Matched from './components/Matched';
import Excluded from './components/Excluded';
import { steps } from './utils/tourGuideSteps';
import ForReview from './components/ForReview';
import PaymentPlan from './components/PaymentPlan';
import UploadFileTab from './components/UploadFile';
import ExclusionRules from './components/ExclusionRules';
import { useCustomMediaQuery } from 'hooks/useMediaQuery';

import PurchaseActivity from './components/PurchaseActivity';
import AiMatching from './components/PurchaseAiAgentMatching';
import { inventoryReconciliationStyles } from './style/style';
import JoyrideTour from 'components/Onboarding/Tour/JoyRideTour';

import { inventoryReconciliationTabsData } from './utils/inventoryReconciliationTabsData';

const InventoryReconciliation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const tourId = TOUR_TYPES.INVENTORY_REVIEW;
    const nextTourId = TOUR_TYPES.INVENTORY_UPLOAD;
    const isMobileScreen = useCustomMediaQuery('(max-width: 600px)');
    const classes = inventoryReconciliationStyles({ isMobileScreen });
    const tourStatus = useSelector((s) => selectTourStatus(s, tourId));
    const isSkipped = useSelector((state) => selectIsSkipped(state, tourId));
    const tutorialPreference = useSelector((state) => selectTutorialPreference(state));
    const isNextTourCompleted = useSelector((state) => selectIsCompleted(state, nextTourId));

    const [runTour, setRunTour] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [activeTab, setActiveTab] = useState('for-review');

    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
        navigate(`/purchases/${tabValue}`);
    };

    useEffect(() => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        const tabFromUrl = pathParts[1] || 'for-review';

        if (inventoryReconciliationTabsData.some((tab) => tab.value === tabFromUrl)) {
            setActiveTab(tabFromUrl);
        } else {
            navigate('/purchases/for-review', { replace: true });
        }
    }, [location.pathname, navigate]);

    useEffect(() => {
        const manualRestart = tourStatus?.manuallyStarted;

        // Only auto-skip if not manually started
        if ((tutorialPreference === 'skipped' || isSkipped) && !manualRestart) {
            setRunTour(false);
            return;
        }

        const timer = setTimeout(() => {
            if (manualRestart) {
                setCurrentStep(0);
                dispatch(startTour({ tourId }));
                setRunTour(true);
            } else if (!tourStatus?.completed) {
                if (tourStatus?.started) {
                    setRunTour(true);
                    setCurrentStep(tourStatus.currentStep);
                } else {
                    setRunTour(false);
                }
            } else {
                setRunTour(false);
            }
        }, 1500);

        return () => clearTimeout(timer);
    }, [dispatch, tourStatus, tutorialPreference, isSkipped]);

    useEffect(() => {
        if (location.state?.defaultTab) {
            const tabFromState = location.state.defaultTab;
            if (inventoryReconciliationTabsData.some((tab) => tab.value === tabFromState)) {
                setActiveTab(tabFromState);
                navigate(`/purchases/${tabFromState}`, { replace: true });
            }
        }
    }, [location.state, navigate]);

    return (
        <Fragment>
            <JoyrideTour
                steps={steps}
                styles={{
                    options: {
                        zIndex: 9999,
                    },
                    tooltip: {},
                }}
                run={runTour}
                stepIndex={currentStep}
                continuous
                scrollToFirstStep
                showBeacon={true}
                spotlightClicks={true}
                callback={(event) => {
                    if (event.action === 'skip' || event.type === 'tour:end') {
                        setRunTour(false);
                        dispatch(completeTour({ tourId }));
                    }
                }}
                onStepBefore={(data) => {
                    const { index, action } = data;
                    if (action === 'next' || action === 'prev') {
                        if (index === 1 || (index === 0 && action === 'prev')) {
                            setActiveTab('for-review');
                        } else if (index === 2 || (index === 1 && action === 'prev')) {
                            setActiveTab('file-upload');
                        }
                    }

                    setCurrentStep(index);
                    dispatch(updateTourStep({ tourId, stepIndex: index }));
                }}
                onFinish={() => {
                    setRunTour(false);

                    if (!isNextTourCompleted) {
                        dispatch(
                            goToNextTour({
                                currentTourId: tourId,
                                nextTourId: TOUR_TYPES.INVENTORY_UPLOAD,
                            }),
                        );
                        setActiveTab('file-upload');
                    } else {
                        dispatch(completeTour({ tourId }));
                    }
                }}
            />

            <Box padding={1} sx={classes.menuWrapper}>
                <Stack
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    gap={1}
                    width={{ xs: '100%', md: 'fit-content' }}
                    style={classes.stack}
                    alignItems="center"
                >
                    {inventoryReconciliationTabsData?.map((tab) => (
                        <Grid item key={tab.label} borderRadius={'5px'}>
                            <Button
                                data-tour={
                                    tab.value === 'for-review'
                                        ? 'for-review-tab'
                                        : tab.value === 'file-upload'
                                            ? 'file-upload-tab'
                                            : undefined
                                }
                                onClick={() => handleTabChange(tab.value)}
                                sx={
                                    tab.value == activeTab ? classes.activeTab : classes.inactiveTab
                                }
                                variant={activeTab === tab.value ? 'contained' : 'text'}
                            >
                                {tab.label}
                                {tab.isBeta && (
                                    <Chip
                                        label="Beta"
                                        size="small"
                                        color="primary"
                                        sx={{
                                            ml: 1,
                                            height: '14px',
                                            fontSize: '0.5rem',

                                        }}
                                    />
                                )}
                            </Button>
                        </Grid>
                    ))}
                </Stack>
            </Box>

            {activeTab === 'file-upload' && (
                <Box sx={classes.tabContentWrapper}>
                    <UploadFileTab />
                </Box>
            )}

            {activeTab === 'ai-matching' && (
                <Box sx={classes.tabContentWrapper}>
                    <AiMatching />
                </Box>
            )}

            {activeTab === 'for-review' && (
                <Box sx={classes.tabContentWrapper}>
                    <Box data-tour="agent-match-button">
                        <ForReview />
                    </Box>
                </Box>
            )}

            {activeTab === 'matched' && (
                <Box sx={classes.tabContentWrapper}>
                    <Matched />
                </Box>
            )}

            {activeTab === 'exclusion-rules' && (
                <Box sx={classes.tabContentWrapper}>
                    <ExclusionRules />
                </Box>
            )}

            {activeTab === 'excluded' && (
                <Box sx={classes.tabContentWrapper}>
                    <Excluded />
                </Box>
            )}


            {activeTab === 'purchase-activity' && (
                <Box sx={classes.tabContentWrapper}>
                    <PurchaseActivity />
                </Box>
            )}

            {activeTab === 'payment-plan' && (
                <Box sx={classes.tabContentWrapper}>
                    <PaymentPlan />
                </Box>
            )}
        </Fragment>
    );
};

export default InventoryReconciliation;
